/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-18
 */

// Add third-party dependencies.
import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Link as LinkIcon,
} from '@mui/icons-material';

// Add local dependencies.
import {
  useGetChecklistMessagesQuery,
  useGetChecklistMessageQuery,
  useDeleteChecklistMessageMutation,
  useSendChecklistMessageMutation,
} from '../../../api/classEssentialsApi';
import { API_BASE_URL, API_VERSION } from '../../../config/api';
import type { ChecklistMessage } from '../../../types/classEssentials';
import ChecklistMessageDialog from './ChecklistMessageDialog';
import ConfirmDialog from './ConfirmDialog';

/**
 * Props for ChecklistMessagesManagement component
 */
interface ChecklistMessagesManagementProps {
  studioId: string;
}

/**
 * Checklist Messages Management component.
 * Displays and manages checklist messages for a studio.
 *
 * @param props - Component props
 * @returns {React.ReactElement} The checklist messages management component
 */
const ChecklistMessagesManagement: React.FC<
  ChecklistMessagesManagementProps
> = ({ studioId }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(
    null
  );
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
  } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const {
    data: messages = [],
    isLoading,
    error,
  } = useGetChecklistMessagesQuery(studioId);

  // Fetch full message details when editing
  const { data: fullMessage } = useGetChecklistMessageQuery(
    selectedMessageId || '',
    {
      skip: !selectedMessageId,
    }
  );

  const [deleteMessage] = useDeleteChecklistMessageMutation();
  const [sendMessage] = useSendChecklistMessageMutation();

  const handleCreate = () => {
    setSelectedMessageId(null);
    setDialogOpen(true);
  };

  const handleEdit = (message: ChecklistMessage) => {
    setSelectedMessageId(message.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    setConfirmAction({
      title: 'Delete Message',
      message:
        'Are you sure you want to delete this message? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteMessage(id).unwrap();
          setSnackbarMessage('Message deleted successfully');
          setSnackbarOpen(true);
        } catch (err) {
          setSnackbarMessage('Failed to delete message');
          setSnackbarOpen(true);
        }
        setConfirmDialogOpen(false);
      },
    });
    setConfirmDialogOpen(true);
  };

  const handleSend = async (id: string) => {
    setConfirmAction({
      title: 'Send Message',
      message:
        'Are you sure you want to send this message to all recipients? They will receive an email with a link to view the checklist.',
      onConfirm: async () => {
        try {
          await sendMessage(id).unwrap();
          setSnackbarMessage('Message sent successfully');
          setSnackbarOpen(true);
        } catch (err) {
          setSnackbarMessage('Failed to send message');
          setSnackbarOpen(true);
        }
        setConfirmDialogOpen(false);
      },
    });
    setConfirmDialogOpen(true);
  };

  const handleCopyLink = async (messageId: string) => {
    // Fetch full message to get unique_token
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/${API_VERSION}/class-essentials/messages/${messageId}/`,
        {
          credentials: 'include',
        }
      );
      const message = await response.json();

      if (message.unique_token) {
        const parentLink = `${window.location.origin}/class-essentials/view/${message.unique_token}`;
        await navigator.clipboard.writeText(parentLink);
        setSnackbarMessage('Parent view link copied to clipboard');
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage('Error: Could not get parent view link');
        setSnackbarOpen(true);
      }
    } catch (err) {
      setSnackbarMessage('Error: Failed to copy parent view link');
      setSnackbarOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedMessageId(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load checklist messages. Please try again.
      </Alert>
    );
  }

  return (
    <>
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography variant="h6">Checklist Messages</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            Create Message
          </Button>
        </Box>

        {messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No checklist messages yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your first checklist message to send to parents.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {messages.map(message => (
                  <TableRow key={message.id}>
                    <TableCell>
                      {message.title || (
                        <Typography variant="body2" color="text.secondary">
                          Untitled
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={message.is_sent ? 'Sent' : 'Draft'}
                        color={message.is_sent ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{message.item_count || 0}</TableCell>
                    <TableCell>{message.recipient_count || 0}</TableCell>
                    <TableCell>
                      {new Date(message.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      {!message.is_sent && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(message)}
                            title="Edit message"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleSend(message.id)}
                            title="Send message"
                            color="primary"
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {message.is_sent && (
                        <IconButton
                          size="small"
                          onClick={() => handleCopyLink(message.id)}
                          title="Copy parent view link"
                          color="info"
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(message.id)}
                        title="Delete message"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <ChecklistMessageDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        studioId={studioId}
        message={fullMessage || null}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        title={confirmAction?.title || ''}
        message={confirmAction?.message || ''}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={() => confirmAction?.onConfirm()}
        onCancel={() => setConfirmDialogOpen(false)}
        severity="warning"
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default ChecklistMessagesManagement;
